import React, { useMemo, useEffect, useRef } from 'react';
import moment from 'moment';
import countriesData from "../../Config/countries.json";

const MailBoxChatWindow = ({
    activeChat,
    messages,
    repliesMap,
    loadingReplies,
    sendingReply,
    newReplyText,
    setNewReplyText,
    onSendReply,
    onDeleteContact,
    onBackToList,
    onUserClick,
    user,
    t
}) => {
    const chatBodyRef = useRef(null);

    useEffect(() => {
        // Initialize Redactor rich text editor on mount
        window.createRedactor('#ReplyRedactor', '');
    }, []);

    useEffect(() => {
        // Clear Redactor editor when active chat changes
        window.cleanRedactor('#ReplyRedactor');
    }, [activeChat.Id]);

    const getCountryFlag = (countryCode) => {
        if (!countryCode) return "";
        const code = countryCode.toLowerCase();
        const found = countriesData.countries.find(c => c.value === code || c.alpha3?.toLowerCase() === code);
        return found ? found.icon : `/assets/flags/32x32/${code}.png`;
    };

    const parseDate = (dateStr) => {
        if (!dateStr) return 0;
        const formats = ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss.SSSZ"];
        const parsed = moment(dateStr, formats, true);
        return parsed.isValid() ? parsed.valueOf() : new Date(dateStr).getTime() || 0;
    };

    const formatMsgClock = (dateStr) => {
        if (!dateStr) return "";
        const m = moment(dateStr, ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss.SSSZ"]);
        return m.isValid() ? m.clone().locale('ar').format("hh:mm A") : dateStr;
    };

    const getSeparatorDateText = (dateStr) => {
        if (!dateStr) return "";
        const m = moment(dateStr, ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss.SSSZ"]);
        if (!m.isValid()) return dateStr;

        const mAr = m.clone().locale('ar');
        if (m.isSame(moment(), 'day')) {
            return t("range.Today") || "اليوم";
        } else if (m.isSame(moment().subtract(1, 'day'), 'day')) {
            return t("range.Yesterday") || "الأمس";
        } else {
            return mAr.format("LL"); // Localized Arabic full date
        }
    };

    // Combine all incoming emails and outgoing replies into a chronological list
    const combinedMessages = useMemo(() => {
        const list = [];
        if (!messages) return list;

        messages.forEach(c => {
            // Add the contact message (incoming)
            list.push({
                id: `contact_${c.Id}`,
                realId: c.Id,
                type: 'incoming',
                title: c.Title,
                body: c.Details,
                date: c.CreationDate,
                timestamp: parseDate(c.CreationDate),
                sender: c.Sender,
                email: c.Email,
                country: c.Country,
                schoolName: c.FromUser?.School?.Name,
                userTypeName: c.FromUser?.UserType?.Name
            });

            // Add replies (outgoing)
            const replies = repliesMap[c.Id] || [];
            replies.forEach(r => {
                list.push({
                    id: `reply_${r.Id}`,
                    realId: r.Id,
                    type: 'outgoing',
                    body: r.Body,
                    date: r.CreationDate,
                    timestamp: parseDate(r.CreationDate),
                    sender: r.User?.Name || 'Admin'
                });
            });
        });

        // Sort: oldest first, newest at the bottom of the chat window
        return list.sort((a, b) => a.timestamp - b.timestamp);
    }, [messages, repliesMap]);

    useEffect(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
        // Small delay as backup for images/rich content loading
        const timer = setTimeout(() => {
            if (chatBodyRef.current) {
                chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [combinedMessages]);

    const chatName = activeChat.name || activeChat.email;
    const initials = chatName.substring(0, 1).toUpperCase();
    const photoUrl = activeChat.photo;
    const countryCode = activeChat.country;

    const adminPhoto = user?.user?.Photo || null;
    const adminName = user?.user?.Name || 'Admin';
    const adminInitials = adminName.substring(0, 1).toUpperCase();

    return (
        <div className="whatsapp-main-chat">
            {/* Header */}
            <div className="whatsapp-chat-header">
                <div className="whatsapp-chat-header-info">
                    {onBackToList && (
                        <button
                            type="button"
                            className="btn btn-link p-0 ml-2 whatsapp-back-btn text-dark"
                            onClick={onBackToList}
                            title={t("Close") || "رجوع"}
                            style={{ fontSize: '20px', border: 'none', background: 'transparent', cursor: 'pointer' }}
                        >
                            <i className="fal fa-arrow-right"></i>
                        </button>
                    )}
                    <div 
                        className="whatsapp-avatar ml-3"
                        style={{ cursor: activeChat.user ? 'pointer' : 'default' }}
                        onClick={() => onUserClick && activeChat.user && onUserClick(activeChat.user)}
                    >
                        {photoUrl ? (
                            <img src={photoUrl} alt={chatName} className="whatsapp-avatar-img" />
                        ) : (
                            initials
                        )}
                    </div>
                    <div
                        className="whatsapp-chat-user-details"
                        style={{ cursor: activeChat.user ? 'pointer' : 'default' }}
                        onClick={() => onUserClick && activeChat.user && onUserClick(activeChat.user)}
                    >
                        <div className="whatsapp-chat-name-row">
                            <span className="whatsapp-chat-name">{chatName}</span>
                            {countryCode && (
                                <img
                                    src={getCountryFlag(countryCode)}
                                    alt={countryCode}
                                    className="whatsapp-flag-icon ml-1"
                                    title={countryCode.toUpperCase()}
                                />
                            )}
                        </div>
                        {activeChat.user && (
                            <span className="whatsapp-chat-status">{t("Pages.MailBox.registeredUser") || "مستخدم مسجل"}</span>
                        )}
                    </div>
                </div>
                <div className="whatsapp-chat-actions">
                    <button
                        type="button"
                        className="btn btn-link text-danger p-0"
                        onClick={() => onDeleteContact(activeChat.id)}
                        title={t("Delete") || "حذف"}
                        style={{ fontSize: '18px', border: 'none', background: 'transparent', cursor: 'pointer' }}
                    >
                        <i className="fal fa-trash-alt"></i>
                    </button>
                </div>
            </div>

            {/* Chat Body (Messages bubble list) */}
            <div className="whatsapp-chat-body" ref={chatBodyRef}>
                {loadingReplies ? (
                    <div className="text-center py-5 my-auto">
                        <i className="fa-spin fas fa-spinner text-primary h3"></i>
                    </div>
                ) : combinedMessages.length === 0 ? (
                    <div className="text-center py-5 my-auto text-muted">
                        <p>{t("NoInfo")}</p>
                    </div>
                ) : (() => {
                    let lastDateStr = null;
                    return combinedMessages.map(msg => {
                        const isOutgoing = msg.type === 'outgoing';
                        const bubbleClass = isOutgoing ? 'whatsapp-msg-outgoing' : 'whatsapp-msg-incoming';

                        // Parse simple day to decide separators
                        const currentDateSeparatorText = getSeparatorDateText(msg.date);
                        const showSeparator = currentDateSeparatorText !== lastDateStr;
                        if (showSeparator) {
                            lastDateStr = currentDateSeparatorText;
                        }

                        return (
                            <React.Fragment key={msg.id}>
                                {showSeparator && (
                                    <div className="mailbox-date-separator">
                                        <span className="badge badge-light px-3 py-2 text-muted" style={{ borderRadius: '12px', fontWeight: 'normal', fontSize: '11px', backgroundColor: '#e1e3e6' }}>
                                            {currentDateSeparatorText}
                                        </span>
                                    </div>
                                )}

                                <div className={`whatsapp-message-bubble-wrapper ${isOutgoing ? 'justify-content-end' : 'justify-content-start'} d-flex align-items-end mb-3`}>
                                    {isOutgoing && (
                                        <div className="whatsapp-avatar mr-2" style={{ width: '28px', height: '28px', fontSize: '11px', flexShrink: 0 }}>
                                            {adminPhoto ? (
                                                <img src={adminPhoto} alt={adminName} className="whatsapp-avatar-img" />
                                            ) : (
                                                adminInitials
                                            )}
                                        </div>
                                    )}
                                    <div className={`whatsapp-message-bubble ${bubbleClass} position-relative px-3 pt-2 pb-4`} style={{ maxWidth: '75%', borderRadius: '12px', minWidth: '80px' }}>
                                        {/* Sender Name (for incoming/outgoing) */}
                                        <div className="whatsapp-sender-name font-weight-bold mb-1" style={{ fontSize: '11.5px', color: isOutgoing ? '#056162' : '#0275d8' }}>
                                            {msg.sender}
                                        </div>

                                        {/* Contact subject title */}
                                        {msg.title && (
                                            <div className="mailbox-message-subject mb-2" style={{ fontSize: '12.5px', borderBottom: '1px solid rgba(0,0,0,0.06)', paddingBottom: '4px' }}>
                                                <i className="fal fa-info-circle text-muted ml-1" style={{ fontSize: '12px' }}></i>
                                                <strong> {msg.title}</strong>
                                            </div>
                                        )}

                                        {/* Message Body */}
                                        <div
                                            className="mailbox-message-content"
                                            dangerouslySetInnerHTML={{ __html: msg.body }}
                                        />

                                        {/* Time of message at the bottom right/left corner */}
                                        <span className="whatsapp-message-time">
                                            {formatMsgClock(msg.date)}
                                        </span>
                                    </div>
                                    {!isOutgoing && (
                                        <div className="whatsapp-avatar ml-2" style={{ width: '28px', height: '28px', fontSize: '11px', flexShrink: 0 }}>
                                            {photoUrl ? (
                                                <img src={photoUrl} alt={chatName} className="whatsapp-avatar-img" />
                                            ) : (
                                                initials
                                            )}
                                        </div>
                                    )}
                                </div>
                            </React.Fragment>
                        );
                    });
                })()}
            </div>

            {/* Input Bar */}
            <form className="whatsapp-input-bar redactor-active" onSubmit={onSendReply}>
                <div className="w-100 redactor-editor-wrapper" style={{ direction: 'rtl', textAlign: 'right' }}>
                    <textarea
                        id="ReplyRedactor"
                        rows={3}
                        className="form-control"
                        placeholder={t("Pages.WhatsApp.typeMessagePlaceholder") || "اكتب ردًا..."}
                        disabled={sendingReply}
                    />
                </div>
                <div className="d-flex justify-content-end mt-1 w-100">
                    <button
                        type="submit"
                        className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1 px-3 py-1-5"
                        disabled={sendingReply}
                        style={{ borderRadius: '8px', fontWeight: '600' }}
                        title={t("Pages.MailBox.send") || "إرسال"}
                    >
                        {sendingReply ? (
                            <i className="fa-spin fas fa-spinner ml-1"></i>
                        ) : (
                            <i className="fal fa-paper-plane ml-1" style={{ transform: 'scaleX(-1)' }}></i>
                        )}
                        <span>{t("Pages.MailBox.send") || "إرسال"}</span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default MailBoxChatWindow;
